import { getAuthSession } from "./browser-auth";

export type LocalPlanKey = "basic" | "pro";

export interface LocalPayment {
  id: string;
  orderNumber: string;
  userLoginId: string;
  memberName: string;
  mobile: string;
  planKey: LocalPlanKey;
  planName: string;
  amount: number;
  status: "결제 완료";
  paymentMethod: "카드결제";
  paidAt: string;
  effectiveAt?: string;
}

export interface LocalInquiry {
  id: string;
  userLoginId: string;
  category: string;
  title: string;
  content: string;
  status: "답변대기" | "답변완료";
  createdAt: string;
  answer?: string;
  answeredAt?: string;
}

export interface LocalCreation {
  id: string;
  userLoginId: string;
  type: string;
  productFileName: string;
  productDescription: string;
  imageDataUrl: string;
  optionsSummary: string;
  status: "생성 완료";
  createdAt: string;
}

export interface LocalModelRequest {
  id: string;
  userLoginId: string;
  modelName: string;
  modelImage: string;
  modelDetail: string;
  modelInfo: string;
  desiredDate: string;
  desiredTime: string;
  status: "요청 완료";
  createdAt: string;
}

const paymentsKey = "art4u:local-payments";
const inquiriesKey = "art4u:local-inquiries";
const creationsKey = "art4u:local-creations";
const modelRequestsKey = "art4u:local-model-requests";
const selectedPaymentKey = "art4u:selected-payment";
const selectedInquiryKey = "art4u:selected-inquiry";
const selectedCreationKey = "art4u:selected-creation";
const selectedModelRequestKey = "art4u:selected-model-request";

function readList<T>(key: string) {
  const value = window.localStorage.getItem(key);
  if (!value) return [] as T[];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed as T[] : [];
  } catch {
    window.localStorage.removeItem(key);
    return [] as T[];
  }
}

function makeId(prefix: string) {
  if ("randomUUID" in window.crypto) return `${prefix}-${window.crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function currentLoginId() {
  return getAuthSession()?.loginId ?? "";
}

function paymentEffectiveTime(payment: LocalPayment) {
  const effectiveTime = Date.parse(payment.effectiveAt ?? payment.paidAt);
  return Number.isNaN(effectiveTime) ? 0 : effectiveTime;
}

function nextMonthStart(reference: Date) {
  return new Date(reference.getFullYear(), reference.getMonth() + 1, 1).toISOString();
}

export function createLocalPayment(input: Omit<LocalPayment, "id" | "userLoginId" | "status" | "paymentMethod" | "paidAt" | "effectiveAt">) {
  const userLoginId = currentLoginId();
  if (!userLoginId) throw new Error("로그인이 필요합니다.");

  const paidAt = new Date();
  const storedPayments = readList<LocalPayment>(paymentsKey);
  const hasCurrentPlan = storedPayments.some((payment) => (
    payment.userLoginId === userLoginId && paymentEffectiveTime(payment) <= paidAt.getTime()
  ));
  const payment: LocalPayment = {
    ...input,
    id: makeId("payment"),
    userLoginId,
    status: "결제 완료",
    paymentMethod: "카드결제",
    paidAt: paidAt.toISOString(),
    effectiveAt: hasCurrentPlan ? nextMonthStart(paidAt) : paidAt.toISOString()
  };
  window.localStorage.setItem(paymentsKey, JSON.stringify([payment, ...storedPayments]));
  selectLocalPayment(payment.id);
  return payment;
}

export function getLocalPayments() {
  const userLoginId = currentLoginId();
  return userLoginId
    ? readList<LocalPayment>(paymentsKey)
      .filter((payment) => payment.userLoginId === userLoginId)
      .map((payment) => ({ ...payment, paymentMethod: "카드결제" as const }))
    : [];
}

export function getCurrentLocalPayment(reference = new Date()) {
  const referenceTime = reference.getTime();
  return getLocalPayments()
    .filter((payment) => paymentEffectiveTime(payment) <= referenceTime)
    .sort((left, right) => (
      paymentEffectiveTime(right) - paymentEffectiveTime(left)
      || Date.parse(right.paidAt) - Date.parse(left.paidAt)
    ))[0] ?? null;
}

export function getPendingLocalPayment(reference = new Date()) {
  const referenceTime = reference.getTime();
  return getLocalPayments()
    .filter((payment) => paymentEffectiveTime(payment) > referenceTime)
    .sort((left, right) => (
      paymentEffectiveTime(left) - paymentEffectiveTime(right)
      || Date.parse(right.paidAt) - Date.parse(left.paidAt)
    ))[0] ?? null;
}

export function selectLocalPayment(id: string) {
  window.sessionStorage.setItem(selectedPaymentKey, id);
}

export function getSelectedLocalPayment() {
  const selectedId = window.sessionStorage.getItem(selectedPaymentKey);
  return getLocalPayments().find((payment) => payment.id === selectedId) ?? null;
}

export function createLocalInquiry(input: Pick<LocalInquiry, "category" | "title" | "content">) {
  const userLoginId = currentLoginId();
  if (!userLoginId) throw new Error("로그인이 필요합니다.");

  const inquiry: LocalInquiry = {
    ...input,
    id: makeId("inquiry"),
    userLoginId,
    status: "답변대기",
    createdAt: new Date().toISOString()
  };
  window.localStorage.setItem(inquiriesKey, JSON.stringify([inquiry, ...readList<LocalInquiry>(inquiriesKey)]));
  selectLocalInquiry(inquiry.id);
  return inquiry;
}

export function getLocalInquiries() {
  const userLoginId = currentLoginId();
  return userLoginId
    ? readList<LocalInquiry>(inquiriesKey).filter((inquiry) => inquiry.userLoginId === userLoginId)
    : [];
}

export function selectLocalInquiry(id: string) {
  window.sessionStorage.setItem(selectedInquiryKey, id);
}

export function getSelectedLocalInquiry() {
  const selectedId = window.sessionStorage.getItem(selectedInquiryKey);
  return getLocalInquiries().find((inquiry) => inquiry.id === selectedId) ?? null;
}

export function upsertLocalCreation(input: Omit<LocalCreation, "userLoginId" | "status" | "createdAt">) {
  const userLoginId = currentLoginId();
  if (!userLoginId) throw new Error("로그인이 필요합니다.");

  const existing = readList<LocalCreation>(creationsKey);
  const previous = existing.find((creation) => creation.id === input.id && creation.userLoginId === userLoginId);
  const creation: LocalCreation = {
    ...input,
    userLoginId,
    status: "생성 완료",
    createdAt: previous?.createdAt ?? new Date().toISOString()
  };
  const next = [creation, ...existing.filter((item) => item.id !== creation.id)];

  const retained = next.slice(0, 10);
  let lastError: unknown;
  while (retained.length > 0) {
    try {
      window.localStorage.setItem(creationsKey, JSON.stringify(retained));
      selectLocalCreation(creation.id);
      return creation;
    } catch (error) {
      lastError = error;
      if (retained.length === 1) break;
      retained.pop();
    }
  }
  throw new Error("브라우저 이미지 저장 공간이 부족합니다. 기존 보관함 데이터를 정리해 주세요.", { cause: lastError });
}

export function getLocalCreations() {
  const userLoginId = currentLoginId();
  return userLoginId
    ? readList<LocalCreation>(creationsKey).filter((creation) => creation.userLoginId === userLoginId)
    : [];
}

export function selectLocalCreation(id: string) {
  window.sessionStorage.setItem(selectedCreationKey, id);
}

export function getSelectedLocalCreation() {
  const selectedId = window.sessionStorage.getItem(selectedCreationKey);
  return getLocalCreations().find((creation) => creation.id === selectedId) ?? null;
}

export function createLocalModelRequest(
  input: Omit<LocalModelRequest, "id" | "userLoginId" | "status" | "createdAt">
) {
  const userLoginId = currentLoginId();
  if (!userLoginId) throw new Error("로그인이 필요합니다.");

  const request: LocalModelRequest = {
    ...input,
    id: makeId("model-request"),
    userLoginId,
    status: "요청 완료",
    createdAt: new Date().toISOString()
  };
  window.localStorage.setItem(
    modelRequestsKey,
    JSON.stringify([request, ...readList<LocalModelRequest>(modelRequestsKey)])
  );
  selectLocalModelRequest(request.id);
  return request;
}

export function getLocalModelRequests() {
  const userLoginId = currentLoginId();
  return userLoginId
    ? readList<LocalModelRequest>(modelRequestsKey)
      .filter((request) => request.userLoginId === userLoginId)
    : [];
}

export function selectLocalModelRequest(id: string) {
  window.sessionStorage.setItem(selectedModelRequestKey, id);
}

export function getSelectedLocalModelRequest() {
  const selectedId = window.sessionStorage.getItem(selectedModelRequestKey);
  return getLocalModelRequests().find((request) => request.id === selectedId) ?? null;
}
